import axios from 'axios'

/**
 * @typedef {Object} RpcClientConfig
 * @property {string} baseURL
 * @property {number} [timeout]
 * @property {Record<string, string>} [headers]
 * @property {string} [methodPrefix] - Prefix to prepend to method names (e.g., 'admin.')
 * @property {string[]} [excludeFromPrefix] - Methods that should NOT have the prefix applied
 * @property {(error: import('@monorepo/shared-types').JsonRpcError) => void} [onError]
 * @property {(request: import('@monorepo/shared-types').JsonRpcRequest) => void} [onRequest]
 * @property {(response: import('@monorepo/shared-types').JsonRpcResponse) => void} [onResponse]
 */

/**
 * Coerce a list that PHP may have serialised as an object with non-sequential
 * keys back into an array of strings.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
function toStringList(value) {
  const raw = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? Object.values(value)
      : []

  // Coerce and drop blanks: the server sends roles/actions as either a list or
  // an id-keyed map, and permission checks downstream compare strings. Letting
  // a stray number or null through here would silently fail those comparisons.
  return raw.filter((entry) => entry !== null && entry !== undefined).map((entry) => String(entry))
}

export class BaseRpcClient {
  /**
   * @param {RpcClientConfig} config
   */
  constructor(config) {
    this.config = config
    this.requestId = 1

    this.axios = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
      withCredentials: true, // Required for CORS with Sanctum
    })

    // Request interceptor
    this.axios.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = this.getAuthToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor — detect HTTP 401 and call handleUnauthorized()
    this._unauthorizedHandled = false
    this._unauthorizedTimeout = null
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !this._unauthorizedHandled) {
          this._unauthorizedHandled = true
          // Clear any previous timer, then reset the flag after 2s
          // so future 401s aren't permanently blocked
          if (this._unauthorizedTimeout) clearTimeout(this._unauthorizedTimeout)
          this._unauthorizedTimeout = setTimeout(() => { this._unauthorizedHandled = false }, 2000)
          this.handleUnauthorized()
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Make a JSON-RPC call
   * @template T
   * @param {string} method
   * @param {any} [params]
   * @param {import('axios').AxiosRequestConfig} [options]
   * @returns {Promise<T>}
   */
  async call(method, params, options = {}) {
    const request = this.buildRequest(method, params)

    if (this.config.onRequest) {
      this.config.onRequest(request)
    }

    const endpoint = this.endpointFor(method)

    try {
      const response = await this.axios.post(
        endpoint,
        request,
        options
      )

      const rpcResponse = response.data

      if (this.config.onResponse) {
        this.config.onResponse(rpcResponse)
      }

      if (rpcResponse.error) {
        this.throwRpcError(rpcResponse.error)
      }

      return rpcResponse.result
    } catch (error) {
      if (error instanceof RpcError) {
        throw error
      }

      this.throwTransportError(error)
    }
  }

  /**
   * Build the JSON-RPC envelope, applying the configured method prefix unless
   * this method is excluded from it.
   *
   * @param {string} method
   * @param {any} params
   * @returns {import('@monorepo/shared-types').JsonRpcRequest}
   */
  buildRequest(method, params) {
    const excludeFromPrefix = this.config.excludeFromPrefix || []
    const methodPrefix = this.config.methodPrefix || ''
    const prefixedMethod =
      methodPrefix && !excludeFromPrefix.includes(method) ? methodPrefix + method : method

    return {
      jsonrpc: '2.0',
      method: prefixedMethod,
      params,
      id: this.requestId++,
    }
  }

  /**
   * Choose the RPC endpoint for a method.
   *
   * Public methods don't require authentication, and the pre-auth helpers
   * carry their own short-lived token (`challenge_token` / `setup_token` /
   * `change_token`). Those MUST hit `/public` even when a stale `*_token`
   * lingers in localStorage from a prior session — otherwise the request
   * gains an `Authorization` header the public procedures don't expect.
   *
   * Matched on the ORIGINAL (unprefixed) method name.
   *
   * @param {string} method
   * @returns {string}
   */
  endpointFor(method) {
    const publicMethods = [
      'auth@login',
      'auth@register',
      'test@ping',
      'health@check',
      'test@handle',
      // MFA pre-auth surface (challenge_token / setup_token only — caller is
      // not yet authenticated).
      'mfa@verify',
      'mfa@startChallenge',
      'mfa@bootstrapTotpInit',
      'mfa@bootstrapTotpConfirm',
      'mfa@bootstrapEmailInit',
      'mfa@bootstrapEmailConfirm',
      'mfa@bootstrapSmsInit',
      'mfa@bootstrapSmsConfirm',
      'mfa@bootstrapWebauthnInit',
      'mfa@bootstrapWebauthnConfirm',
      // Forced password change (#1198): carries its own short-lived
      // `change_token`, and the caller has no access token yet by design.
      'auth@completeForcedPasswordChange',
    ]

    return publicMethods.includes(method) || !this.getAuthToken() ? '/public' : ''
  }

  /**
   * Raise a JSON-RPC error the server returned in a 2xx envelope, signalling
   * the app first if the message reads as an authentication failure.
   *
   * @param {import('@monorepo/shared-types').JsonRpcError} error
   * @returns {never}
   */
  throwRpcError(error) {
    const AUTH_FAILURE_HINTS = [
      'unauthenticated',
      'invalid token',
      'unauthorized',
      'token has been revoked',
      'token not found',
    ]

    if (error.message) {
      const message = error.message.toLowerCase()
      if (AUTH_FAILURE_HINTS.some((hint) => message.includes(hint))) {
        this.handleUnauthorized()
      }
    }

    if (this.config.onError) {
      this.config.onError(error)
    }

    throw new RpcError(error)
  }

  /**
   * Normalise a thrown transport error.
   *
   * An HTTP error status can still carry a JSON-RPC error body — the
   * middleware chain answers that way (401 "Unauthenticated.", 403
   * "Password change required."). Surface the server's own error rather than
   * burying it under a generic "Network error", or callers cannot tell an
   * authorization decision apart from a dropped connection.
   *
   * @param {any} error
   * @returns {never}
   */
  throwTransportError(error) {
    const serverError = error.response?.data?.error

    if (serverError) {
      if (serverError.data?.reason === 'password_change_required') {
        this.handlePasswordChangeRequired()
      }

      this.throwRpcError(serverError)
    }

    /** @type {import('@monorepo/shared-types').JsonRpcError} */
    const rpcError = {
      code: -32603,
      message: `Network error: ${error.message}`,
      data: error.data,
    }

    if (this.config.onError) {
      this.config.onError(rpcError)
    }

    throw new RpcError(rpcError)
  }

  /**
   * Batch multiple RPC calls
   * @template T
   * @param {Array<{ method: string; params?: any }>} calls
   * @returns {Promise<T>}
   */
  async batch(calls) {
    /** @type {import('@monorepo/shared-types').JsonRpcRequest[]} */
    const requests = calls.map((call, index) => ({
      jsonrpc: '2.0',
      method: call.method,
      params: call.params,
      id: this.requestId + index,
    }))

    this.requestId += calls.length

    const response = await this.axios.post(
      '/',
      requests
    )

    const results = []
    const errors = []

    response.data.forEach((rpcResponse) => {
      if (rpcResponse.error) {
        errors.push(new RpcError(rpcResponse.error))
      } else {
        results.push(rpcResponse.result)
      }
    })

    if (errors.length > 0) {
      throw new BatchRpcError(errors)
    }

    return results
  }

  // ───────────────────────────────────────────────────────────────────────
  // MFA helpers (shared across every authenticated frontend client).
  //
  // The pre-auth helpers (verifyMfa, startMfaChallenge, bootstrapTotp*)
  // are listed in `publicMethods` above so they hit `/public` even when
  // a stale auth token still lives in localStorage. The self-service
  // helpers (list / init / confirm / remove / regenerate) require the
  // caller's own access token and rely on the standard authenticated
  // endpoint routing.
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Complete an MFA challenge with a method-specific proof. On success the
   * issued access token is stored on this client (subclasses override
   * `setAuthToken` to choose the storage key).
   *
   * `rememberDevice` is hub-only: when true the backend issues a 14-day
   * trusted-device cookie so the user skips MFA on subsequent logins from
   * the same browser. Admin ignores the flag — admin sessions always
   * re-challenge per the #740 spec.
   *
   * @param {string} challengeToken
   * @param {string} type     'totp' | 'email' | 'sms' | 'webauthn' | 'recovery_code'
   * @param {Object} payload  Method-specific input, e.g. { code: '123456' }
   * @param {boolean} [rememberDevice=false]
   */
  async verifyMfa(challengeToken, type, payload, rememberDevice = false) {
    const result = await this.call('mfa@verify', {
      challenge_token: challengeToken,
      type,
      payload,
      remember_device: rememberDevice === true,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  // ───────────────────────────────────────────────────────────────────────
  // Forced password change (PCI DSS 8.3.5, #1198)
  //
  // Two ways in, because there are two ways to arrive at the requirement:
  //  - `completeForcedPasswordChange` for a login that answered
  //    `password_change_required` — the caller holds a short-lived
  //    `change_token`, no access token, and this hits `/public`;
  //  - `changeOwnPassword` for a session that already holds a token minted
  //    before the flag was set. The middleware keeps `users@changePassword`
  //    reachable precisely so that session is not bricked.
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Complete a forced password change and store the access token login
   * withheld. Returns the full result so callers can hydrate the session.
   *
   * @param {string} changeToken
   * @param {string} currentPassword
   * @param {string} newPassword
   */
  async completeForcedPasswordChange(changeToken, currentPassword, newPassword) {
    const result = await this.call('auth@completeForcedPasswordChange', {
      change_token: changeToken,
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPassword,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  /**
   * Change the authenticated caller's own password. Keeps the current session
   * alive, so a user gated by `must_change_password` can clear the gate
   * without signing in again.
   *
   * @param {string} currentPassword
   * @param {string} newPassword
   */
  async changeOwnPassword(currentPassword, newPassword) {
    return this.call('users@changePassword', {
      data: {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPassword,
      },
    })
  }

  /**
   * Login-time: dispatch the one-time code for an email/SMS method, or
   * return the WebAuthn assertion options. TOTP does not need this —
   * the authenticator already shows the code.
   */
  async startMfaChallenge(challengeToken, type) {
    return this.call('mfa@startChallenge', {
      challenge_token: challengeToken,
      type,
    })
  }

  /**
   * Forced-setup TOTP init (used when the login response is
   * `mfa_setup_required`).
   */
  async bootstrapTotpInit(setupToken, label) {
    return this.call('mfa@bootstrapTotpInit', { setup_token: setupToken, label })
  }

  /**
   * Forced-setup TOTP confirm. On success stores the issued access token
   * and returns the one-time recovery codes.
   */
  async bootstrapTotpConfirm(setupToken, code) {
    const result = await this.call('mfa@bootstrapTotpConfirm', {
      setup_token: setupToken,
      code,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  /**
   * Forced-setup Email-OTP init. Dispatches the verification code to the
   * supplied address; confirm will sync `User.email` to that address.
   */
  async bootstrapEmailInit(setupToken, destination) {
    return this.call('mfa@bootstrapEmailInit', {
      setup_token: setupToken,
      destination,
    })
  }

  /**
   * Forced-setup Email-OTP confirm. On success stores the issued access
   * token and returns the one-time recovery codes.
   */
  async bootstrapEmailConfirm(setupToken, destination, code) {
    const result = await this.call('mfa@bootstrapEmailConfirm', {
      setup_token: setupToken,
      destination,
      code,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  /**
   * Forced-setup SMS-OTP init. Dispatches the code to the supplied number;
   * confirm will sync `User.phone` to that number.
   */
  async bootstrapSmsInit(setupToken, destination) {
    return this.call('mfa@bootstrapSmsInit', {
      setup_token: setupToken,
      destination,
    })
  }

  /**
   * Forced-setup SMS-OTP confirm. On success stores the issued access token
   * and returns the one-time recovery codes.
   */
  async bootstrapSmsConfirm(setupToken, destination, code) {
    const result = await this.call('mfa@bootstrapSmsConfirm', {
      setup_token: setupToken,
      destination,
      code,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  /**
   * Forced-setup WebAuthn registration init: returns the
   * PublicKeyCredentialCreationOptions for `navigator.credentials.create()`.
   */
  async bootstrapWebauthnInit(setupToken) {
    return this.call('mfa@bootstrapWebauthnInit', { setup_token: setupToken })
  }

  /**
   * Forced-setup WebAuthn confirm. `credential` is the full
   * PublicKeyCredential JSON returned by the browser. On success stores the
   * issued access token and returns the one-time recovery codes.
   */
  async bootstrapWebauthnConfirm(setupToken, credential) {
    const result = await this.call('mfa@bootstrapWebauthnConfirm', {
      setup_token: setupToken,
      credential,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  /** List the caller's confirmed MFA methods and remaining recovery codes. */
  async listMfa() {
    return this.call('mfa@list', {})
  }

  /** Start self-service TOTP registration. */
  async initTotp(label) {
    return this.call('mfa@totpInit', label !== undefined ? { label } : {})
  }

  /** Confirm self-service TOTP registration. */
  async confirmTotp(code) {
    return this.call('mfa@totpConfirm', { code })
  }

  /**
   * Start self-service Email-OTP enrolment. Confirm will also update User.email.
   */
  async initEmailMfa(destination) {
    return this.call('mfa@emailInit', { destination })
  }

  async confirmEmailMfa(destination, code) {
    return this.call('mfa@emailConfirm', { destination, code })
  }

  /** Start self-service SMS-OTP enrolment. Confirm will sync User.phone. */
  async initSmsMfa(destination) {
    return this.call('mfa@smsInit', { destination })
  }

  async confirmSmsMfa(destination, code) {
    return this.call('mfa@smsConfirm', { destination, code })
  }

  /**
   * Start self-service WebAuthn (security key / passkey) registration.
   * Returns the PublicKeyCredentialCreationOptions the browser passes to
   * `navigator.credentials.create()`.
   */
  async initWebAuthnMfa() {
    return this.call('mfa@webauthnInit', {})
  }

  /**
   * Confirm self-service WebAuthn registration. `credential` is the
   * full PublicKeyCredential JSON object returned by the browser.
   */
  async confirmWebAuthnMfa(credential) {
    return this.call('mfa@webauthnConfirm', { credential })
  }

  async removeMfaMethod(methodId) {
    return this.call('mfa@removeMethod', { method_id: methodId })
  }

  async regenerateRecoveryCodes() {
    return this.call('mfa@regenerateRecoveryCodes', {})
  }

  /**
   * The authenticated caller's own auth context, normalised.
   *
   * `auth@me` answers `{ user, permissions: { roles, groups, actions,
   * is_admin } }`. Two things about that shape belong here rather than in each
   * app's store:
   *  - PHP's `array_unique()` on the action list preserves keys, so a
   *    non-sequential result is serialised as a JSON OBJECT rather than an
   *    array. Coerce it once.
   *  - `must_change_password` rides on the user object. `auth@me` is on the
   *    forced-change allowlist precisely so a gated session can discover the
   *    obligation before it renders anything (PCI DSS 8.3.5, #1198).
   *
   * Throws on failure like any other call — callers decide whether a stale
   * permission list is better than none.
   *
   * @returns {Promise<{ user: Object | null, actions: string[], roles: string[], is_admin: boolean, must_change_password: boolean }>}
   */
  async getAuthContext() {
    const response = await this.call('auth@me')
    const permissions = response?.permissions ?? {}

    return {
      user: response?.user ?? null,
      actions: toStringList(permissions.actions),
      roles: toStringList(permissions.roles),
      is_admin: permissions.is_admin === true,
      must_change_password: response?.user?.must_change_password === true,
    }
  }

  /**
   * Get auth token from storage
   * @returns {string | null}
   */
  getAuthToken() {
    // Can be overridden in subclasses
    return localStorage.getItem('auth_token')
  }

  /**
   * Handle unauthorized responses
   */
  handleUnauthorized() {
    // Can be overridden in subclasses
    // For example to login, refresh token, etc.
  }

  /**
   * The server refused this request because the user must change their
   * password first (PCI DSS 8.3.5, #1198).
   *
   * Unlike a 401 this is NOT a dead session — the token is valid and the user
   * stays signed in — so nothing is cleared here. The app is signalled the same
   * way as `session-expired` so the router can send them to the forced-change
   * screen from wherever they were.
   */
  handlePasswordChangeRequired() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('password-change-required'))
    }
  }

  /**
   * Set auth token
   * @param {string} token
   */
  setAuthToken(token) {
    localStorage.setItem('auth_token', token)
  }

  /**
   * Clear auth token
   */
  clearAuthToken() {
    localStorage.removeItem('auth_token')
  }
}

/**
 * RPC Error class
 */
export class RpcError extends Error {
  /**
   * @param {import('@monorepo/shared-types').JsonRpcError} error
   */
  constructor(error) {
    super(error.message)
    this.name = 'RpcError'
    this.code = error.code
    this.data = error.data
  }
}

/**
 * Extract a human-readable error message from an RPC error.
 *
 * Checks error.data for field-level validation messages (Laravel format),
 * falls back to error.message, then to the provided fallback string.
 *
 * @param {Error|RpcError} error - The caught error
 * @param {string} fallback - Default message when nothing useful can be extracted
 * @returns {string}
 */
export function extractRpcErrorMessage(error, fallback) {
  if (error.data && typeof error.data === 'object') {
    const messages = Object.values(error.data).flat()
    if (messages.length > 0) {
      return messages.join(' ')
    }
  }
  if (error.message) {
    return error.message
  }
  return fallback
}

/**
 * Batch RPC Error class
 */
export class BatchRpcError extends Error {
  /**
   * @param {RpcError[]} errors
   */
  constructor(errors) {
    super('Batch RPC request failed')
    this.name = 'BatchRpcError'
    this.errors = errors
  }
}